import { create } from 'zustand';
import moment from 'moment';
import { config } from '../config';
import {
  CLIENT_TYPES,
  SESSION_EVENTS,
  SyncClient,
} from '@smarterservices/sync-client';
import { getMediaAuthToken } from '../helpers/utilities';
import CONSTANTS from '../config/constants';
import SessionTrack from '../classes/SessionTrack';
export const createExamSessionStore = (initialState = {}) =>
  create((set, get) => ({
    stores: {},
    sessionSid: null,
    examSid: null,
    installSid: null,
    courseSid: null,
    enrollmentSid: null,
    userSid: null,
    status: null,
    isReconnect: false,
    overlayStack: [],
    currentOverlay: null,
    sessionDuration: 0, // this will represent the duration of the session, if live, it will be the duration plus 30 minutes. If recorded it will be the actual session duration.
    duration: 0,
    activeSessionStartTime: 0, // default this to now - 5 minutes...but this would be the actual field...
    recordingStartDate: null, // The ISO8601 date of when the recording started.
    recordingEndDate: null, // The ISO8601 date of when the recording ended.
    recordingStatus: null, // The status of the recording. Valid options are in CONSTANTS file...
    isMediaServerRecording: false, // Flag that indicates if recording is active on the media server...
    sessionData: {},
    noise: { hasNoise: false }, // if there is any noise being detected from the participant
    parentSessionData: null,
    childSessionData: null,
    incidentReport: null,
    courseData: {},
    userData: {},
    accountData: {},
    sync: {
      client: null,
      doc: null,
      emitter: null,
    },
    journey: {},
    redirectedToExamAt: null,
    annotationTypes: {},
    currentModalityOptions: [], // The modality options that are currently enabled and enforced.
    modalityOptions: {}, // The modality options that are enabled for this exam in the configuration.
    modalityOptionGroups: {}, // This is all possible modality options and their groups
    managingAnnotation: null, // The annotation that is being managed.
    connectionInfo: null,
    identityValidation: null,
    isIdentityValidationUpdating: false,
    isIdentityValidated: false,
    annotations: {
      flags: [],
      events: [],
      notes: [],
      network: [],
      phases: [],
    },

    injectDependentStores: (stores) => {
      set({ stores });
    },

    storeState: (name) => {
      if (!get().stores[name])
        throw new Error(`${name} is not a valid dependency in this store.`);
      return get().stores[name].getState();
    },

    /**
     * Initialize the exam session store with session data and related information.
     *
     * This function orchestrates the loading of all session-related data by making multiple
     * API calls in parallel groups to optimize initialization time. It handles both live and
     * recorded sessions, manages sync connections for live sessions, and loads all necessary
     * data including annotations, journey information, user profiles, and related sessions.
     *
     * **Performance Optimization:**
     * The function uses two parallel execution groups with Promise.allSettled() to significantly
     * reduce initialization time:
     * - **Group 1:** Loads related sessions (parent/child) and recording metadata concurrently
     * - **Group 2:** Loads annotations, journey, course data, identity validation, user profile,
     *   and incident reports concurrently
     *
     * This parallel approach reduces initialization time from ~9 sequential API calls to 2 parallel
     * groups, resulting in approximately 4.5x faster initialization on typical network conditions.
     *
     * **Error Handling:**
     * Uses Promise.allSettled() instead of Promise.all() to ensure that failures in individual
     * API calls don't block the loading of other data. Each failed operation is logged to the
     * console with specific error details for debugging.
     *
     * **Session Flow:**
     * 1. Sets initializing overlay
     * 2. Loads primary session data (sequential - required for all subsequent calls)
     * 3. Checks session status and connects to sync if live session
     * 4. **Parallel Group 1:** Loads related sessions and recording metadata
     * 5. Sets session status and modality options
     * 6. **Parallel Group 2:** Loads all remaining session data concurrently
     * 7. Handles results and updates store state
     *
     * @async
     * @function initialize
     * @param {Object} options - Initialization options
     * @param {string} options.sessionSid - Session SID to initialize
     * @param {string} options.examSid - Exam SID associated with the session
     * @param {string} options.installSid - Installation SID for the session
     * @param {boolean} [options.isReconnect=false] - Whether this is a reconnect session (from VTC proctor sync doc)
     * @returns {Promise<void>}
     *
     * @throws {Error} Logs errors to console but does not throw (graceful degradation)
     *
     * @example
     * await examSessionStore.getState().initialize({
     *   sessionSid: 'session_123',
     *   examSid: 'exam_456',
     *   installSid: 'install_789'
     * });
     *
     * @see {@link loadRelatedSession} For loading parent/child session data
     * @see {@link updateRecordingMetadata} For loading recording information
     * @see {@link loadAnnotations} For loading session annotations
     * @see {@link refreshSessionJourney} For loading journey data
     * @see {@link loadIdentityValidation} For loading identity validation status
     * @see {@link getIncidentReport} For loading incident reports
     */
    initialize: async (options) => {
      const now = new Date();
      const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);

      get().setOverlay({ name: CONSTANTS.OVERLAYS.SESSION.INITIALIZING.NAME });

      const initialSettings = {
        sessionSid: options.sessionSid,
        isReconnect: options.isReconnect || false,
      };

      set(initialSettings);

      // Load the session information....
      const sessionData = await get()
        .storeState('appStore')
        .ApiClient.getSession({
          sessionSid: options.sessionSid,
          examSid: options.examSid,
          installSid: options.installSid,
        });

      if (sessionData.successful) {
        const dataToUpdate = {
          sessionData: sessionData.data,
          examSid: sessionData.data.examSid,
          installSid: sessionData.data.installSid,
          enrollmentSid: sessionData.data.enrollmentSid,
          userSid: sessionData.data.userSid,
          courseSid: sessionData.data.courseSid
        };

        if (
          sessionData.data.status.toLowerCase() === 'closed' ||
          sessionData.data.status.toLowerCase() === 'cancelled'
        ) {
          //dataToUpdate.sessionDuration = sessionData.data?.duration; // TODO: Remove this, it was just a fallback...

          // set the duration on the playback store as well.
          get()
            .storeState('playerStore')
            .setDuration(dataToUpdate.sessionDuration);
          get().storeState('playerStore').setCanHaveLiveVideo(false);
        } else if (CONSTANTS.STATUSES.CAN_HAVE_LIVE_VIDEO.includes(sessionData.data.status.toLowerCase())) {
          get().storeState('playerStore').setCanHaveLiveVideo(true);
          // connect to sync if the session can have live video, that means it is open or in progress...
          await get().connectToSync();
          // remove the initializing overlay as we are passed that...
          get().removeOverlay(CONSTANTS.OVERLAYS.SESSION.INITIALIZING.NAME);
        }

        /*
          Since we are past checking to see if we can have live video, we can set the overlay to loading,
          this way if the session was transfered the transfer overlay will show up first, then if dismissed quickly,
          the loading overlay will show up next.
        */
        get().setOverlay({ name: CONSTANTS.OVERLAYS.SESSION.LOADING.NAME, last: true });
        // in case we are not live, double check by removing the initializing overlay as we are passed that...
        get().removeOverlay(CONSTANTS.OVERLAYS.SESSION.INITIALIZING.NAME);

        set(dataToUpdate);

        // Group 1: Load related sessions and recording metadata in parallel
        const group1Results = await Promise.allSettled([
          get().loadRelatedSession("parent"),
          get().loadRelatedSession("child"),
          get().updateRecordingMetadata(),
        ]);

        // Log any failures from Group 1
        group1Results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const names = ['loadRelatedSession(parent)', 'loadRelatedSession(child)', 'updateRecordingMetadata'];
            console.error(`Failed to load ${names[index]}:`, result.reason);
          }
        });

        get().setStatus(sessionData.data.status);

        // set the modality options up...
        const modalityOptions =
          sessionData.data.examConfiguration.modalityOptions[
          sessionData.data.proctorTypeSid
          ];

        set({ modalityOptions });

        // Group 2: Load critical data in parallel (excluding annotations for faster UI render)
        const group2StartTime = performance.now();
        const group2Results = await Promise.allSettled([
          get().refreshSessionJourney(),
          get()
            .storeState('appStore')
            .ApiClient.getCourse({
              installSid: get().installSid,
              courseSid: get().courseSid,
            }),
          get().loadIdentityValidation(),
          get()
            .storeState('appStore')
            .ApiClient.getUserProfile({
              installSid: get().installSid,
              userSid: get().userSid,
            }),
          get().getIncidentReport(),
        ]);

        // Extract results and handle each one
        const [
          journeyResult,
          courseResult,
          identityResult,
          userResult,
          incidentResult
        ] = group2Results;

        // Handle course data result
        if (courseResult.status === 'fulfilled' && courseResult.value?.successful) {
          set({ courseData: courseResult.value.data });
        } else {
          console.error('Failed to load course data:', courseResult.status === 'rejected' ? courseResult.reason : courseResult.value);
          //TODO: IF this could not be loaded....we need to show an error message...
        }

        // Handle user data result
        if (userResult.status === 'fulfilled' && userResult.value?.successful) {
          set({ userData: userResult.value.data });
        } else {
          console.error('Failed to load user data:', userResult.status === 'rejected' ? userResult.reason : userResult.value);
          //TODO: IF this could not be loaded....we need to show an error message...
        }

        // Log failures for other operations
        if (journeyResult.status === 'rejected') {
          console.error('Failed to load session journey:', journeyResult.reason);
        }
        if (identityResult.status === 'rejected') {
          console.error('Failed to load identity validation:', identityResult.reason);
        }
        if (incidentResult.status === 'rejected') {
          console.error('Failed to load incident report:', incidentResult.reason);
        }

        // Load annotations asynchronously after UI is ready (non-blocking)
        // This allows the navbar, video player, and other UI elements to render immediately
        setTimeout(() => {
          get().loadAnnotations([
            'flags',
            'events',
            'notes',
            'network',
            'phases',
          ]).catch(error => {
            console.error('Failed to load annotations:', error);
          });
        }, 0);

      } else {
        //TODO: IF this could not be loaded....we need to show an error message...
      }
    },

    loadIdentityValidation: async () => {
      set({ isIdentityValidationUpdating: true });
      const identityValidation = await get()
        .storeState('appStore')
        .ApiClient.getIdentityValidation({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
        });

      if (identityValidation.successful) {
        set({
          identityValidation: identityValidation.data,
          isIdentityValidationUpdating: false,
          isIdentityValidated: identityValidation.data.status === 'pass'
        });
      } else {
        //TODO: IF this could not be loaded....we need to show an error message...
        set({ isIdentityValidationUpdating: false });
      }
    },

    updateIdentityValidation: async ({ status }) => {

      const identityValidation = await get()
        .storeState('appStore')
        .ApiClient.updateIdentityValidationStatus({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
          payload: { status, validatedBy: (get().storeState('appStore').user?.firstName || '') + ' ' + (get().storeState('appStore').user?.lastName || '') || 'unknown' }
        });

      if (identityValidation.successful) {
        set({
          isIdentityValidated: status === 'pass'
        });
      } else {
        //TODO: IF this could not be loaded....we need to show an error message...

      }
    },

    loadRelatedSession: async (type) => {
      const sessionData = {
        ...get().sessionData,
      };
      let sessionSid;

      if (type === "parent"
        && sessionData?.series
        && sessionData?.series?.relatedSessions
        && sessionData?.series?.relatedSessions?.parentSid) {
        sessionSid = sessionData?.series?.relatedSessions?.parentSid;
      } else if (type === "child"
        && sessionData?.series
        && sessionData?.series?.relatedSessions
        && sessionData?.series?.relatedSessions?.childSid) {
        sessionSid = sessionData?.series?.relatedSessions?.childSid;
      }

      // If there is a related session, snag the details...
      if (sessionSid) {
        const relatedSessionData = await get()
          .storeState('appStore')
          .ApiClient.getSession({
            sessionSid: sessionSid,
            examSid: get().examSid,
            installSid: get().installSid,
          });

        if (relatedSessionData.successful) {
          if (type === "parent") {
            set({ parentSessionData: relatedSessionData.data });
          } else {
            set({ childSessionData: relatedSessionData.data });
          }
        }
      }
    },

    setRecordingMetadata: (data) => {
      const localSessionData = {
        ...get().sessionData,
      };

      //console.log('setRecordingMetadata', data);

      if (data.startDate) {
        localSessionData.recordingStartDate = data.startDate;
      }
      if (data.status) {
        localSessionData.recordingStatus = data.status;
        set({ recordingStatus: data.status });
      }

      if (data.duration) {
        localSessionData.duration = data.duration;
        get().storeState('playerStore').setDuration(data.duration);
      } else if (data.endDate && localSessionData.recordingStartDate) {
        localSessionData.recordingEndDate = data.endDate;
        const videoDuration = Math.ceil(
          moment
            .utc(localSessionData.recordingEndDate)
            .diff(localSessionData.recordingStartDate, 'seconds', true)
        );
        localSessionData.duration = videoDuration;
        get().storeState('playerStore').setDuration(videoDuration);
      }
      //console.log('setRecordingMetadata', localSessionData);
      set({ sessionData: localSessionData });


      // update the player tracks now...
      if (data?.tracks) {
        Object.keys(data.tracks).forEach((trackId) => {
          const track = data.tracks[trackId];
          const sessionTrack = new SessionTrack();
          sessionTrack.populateFromHls(track);

          get().storeState('playerStore').addTrack(sessionTrack);
        });
      }
      // after updating the recording end date, we need to update the player...
      get().storeState('playerStore').updatePlayer();
    },

    /**
     * Updates the recording status for the current session. This will also update the store state on the playerStore to reflect
     * the recording status. This does not control if the session is actually being recorded, this is only the status of the actual re
     * recorded video.
     * @param {string} status - The current recording status. Should be one of ["live", "complete", "cancelled", "failed"]
     * @returns {undefined}
     */
    setRecordingStatus: (status) => {
      const localSessionData = {
        ...get().sessionData,
      };
      localSessionData.recordingStatus = status;
      set({ recordingStatus: localSessionData });
    },

    updateRecordingMetadata: async () => {
      // get the recording metadata.
      const recordingData = await get()
        .storeState('appStore')
        .ApiClient.getRecordingMetadata({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
        });

      if (recordingData.successful) {
        get().setRecordingMetadata({
          startDate: recordingData.data.startDate,
          endDate: recordingData.data.endDate,
          status: recordingData.data.status,
          duration: recordingData.data.duration,
          tracks: recordingData.data.tracks,
        });
      } else {
        // TODO: Handle error
      }
    },

    setIsMediaServerRecording: async (isMediaServerRecording) => {
      // since the media server is recording, lets setup the base recording dates...
      await get().updateRecordingMetadata();

      set({ isMediaServerRecording });
    },

    getUniqueExamPassword: () => {
      // Generate the unique password which is the last 5 characters of the sessionSid prefixed with sp
      const last5Chars = get().sessionSid.slice(-5);
      return `sp${last5Chars}`;
    },

    connectToSync: async () => {
      const syncClientConfig = {
        apiUrl: config.proctoring.api.baseUrl,
        documentId: `Session_${get().sessionSid}`,
        identity: `viewer_${get().storeState('appStore').identity}_${get().sessionSid}`,
        token: get().storeState('appStore').spToken,
        type: CLIENT_TYPES.SESSION,
      };

      //console.log(`Sync Client Config`, syncClientConfig);

      const localSync = { ...get().sync };

      localSync.client = await SyncClient(syncClientConfig);

      // This was returning stale data sometimes esp with tight read/writes...
      //localSync.doc = await localSync.client.getSync();

      // attach the event emitter to the store...
      localSync.emitter = localSync.client.getEventEmitter();

      // call the API to get the most updated document...
      const syncDocData = await get()
        .storeState('appStore')
        .ApiClient.getSyncDoc({
          sessionSid: get().sessionSid,
        });

      localSync.doc = syncDocData.data.document;

      // set in state...
      set({
        sync: localSync,
        currentModalityOptions: localSync.doc?.currentModalityOptions || [],
      });

      if (localSync.doc?.lastConnectionChange) {
        get().handleLastConnectionChangeUpdated(null, localSync.doc?.lastConnectionChange);
      }

      // setup the sync listeners...
      get().setupSyncListeners();
    },

    refreshSessionJourney: async (options = {}) => {

      const journeyData = await get()
        .storeState('appStore')
        .ApiClient.getSessionJourney({
          sessionSid: get().sessionSid,
          examSid: get().examSid,
          installSid: get().installSid
        });

      if (journeyData.successful) {

        // figure out if the user has been redirected to their exam yet...
        // loop the steps to see if the ExamInProgress step is completed
        const examInProgressStep = journeyData.data?.steps?.find(
          (step) => step.stepId === 'ExamInProgress'
        );

        const hasBeenRedirectedToExam = examInProgressStep?.startTime !== null && examInProgressStep?.completedTime === null;

        if (hasBeenRedirectedToExam) {
          get().storeState('eventStore').trigger('onRedirectedToExam', { journeyStep: examInProgressStep });
        }

        set({ journey: journeyData.data, redirectedToExamAt: examInProgressStep?.startTime });
      } else {
        //TODO: Handle the error
      }
    },

    setSessionAsReviewed: async ({ reviewByUser }) => {
      const reviewCompletedDate = new Date().toISOString();

      const result = await get()
        .storeState('appStore')
        .ApiClient.updateSessionReviewStatus({
          sessionSid: get().sessionSid,
          examSid: get().examSid,
          installSid: get().installSid,
          payload: {
            reviewCompletedDate,
            reviewByUser,
          }
        });

      if (result.successful) {
        // copy session data
        const localSessionData = {
          ...get().sessionData,
        };
        localSessionData.reviewCompletedDate = reviewCompletedDate;
        localSessionData.reviewByUser = reviewByUser;
        set({ sessionData: localSessionData });
      } else {
        //TODO: Handle the error
      }
    },

    /**
     * Gets the current active journey step
     *
     * Retrieves the journey step that matches the current step ID and is still in progress.
     * The step must meet all of the following criteria:
     * - Has an id matching journey.currentStepId
     * - Has type 'standard' (excludes special step types)
     * - Has not been completed (completedTime === null)
     *
     * @returns {Object|null} Returns:
     *   - The current active step object if found and incomplete
     *   - null if no journey exists, current step is completed, or current step ID doesn't match any standard steps
     *
     * @example
     * // Get the current step the user is on
     * const currentStep = ExamSessionStore.getCurrentJourneyStep();
     * if (currentStep) {
     *   console.log('User is currently on:', currentStep.stepId);
     * } else {
     *   console.log('No active step or step is completed');
     * }
     */
    getCurrentJourneyStep: () => {
      const currentStepId = get().journey?.currentStepId;

      if (get().journey?.steps?.length) {
        return get().journey.steps.find(
          (step) =>
            step.id === currentStepId &&
            step.type === 'standard' &&
            step.completedTime === null
        );
      }
      return null;
    },

    /**
     * Checks if a journey step has been started and returns its status
     *
     * This method handles multiple scenarios for journey steps:
     * - Steps that are in progress (started but not completed)
     * - Steps that haven't been started yet
     * - Steps that have been completed
     * - Steps that are being retaken
     *
     * @param {string} stepId - The ID of the journey step to check
     * @returns {Object|null} Returns:
     *   - The incomplete step object if the step is in progress (startTime !== null && completedTime === null)
     *   - null if the step hasn't been started yet (startTime === null)
     *   - null if all matching steps are completed
     *   - null if no matching steps exist
     *
     * @example
     * // Check if identity validation step has been started
     * const step = ExamSessionStore.hasStartedJourneyStep('identity-validation');
     * if (step) {
     *   console.log('Step is in progress:', step);
     * } else {
     *   console.log('Step not started or completed');
     * }
     */
    hasStartedJourneyStep: (stepId) => {
      if (get().journey?.steps?.length) {
        // Find all steps with the given stepId
        const matchingSteps = get().journey.steps.filter(
          (step) => step.stepId === stepId && step.type === 'standard'
        );

        if (matchingSteps.length === 0) {
          return null;
        }

        // Check if there's an incomplete step (started but not completed)
        const incompleteStep = matchingSteps.find(
          (step) => step.startTime !== null && step.completedTime === null
        );

        // If there's an incomplete step, return it
        if (incompleteStep) {
          return incompleteStep;
        }

        // Check if there's an unstarted step (retaking but not started)
        const unstartedStep = matchingSteps.find(
          (step) => step.startTime === null && step.completedTime === null
        );

        // If there's an unstarted step, return null (counts as not started)
        if (unstartedStep) {
          return null;
        }

        // All steps are completed, return null
        return null;
      }
      return null;
    },

    /**
     * Gets the next unstarted journey step after the current one
     *
     * Searches through the journey steps array sequentially to find the next step
     * that meets all the following criteria:
     * - Has type 'standard' (excludes special step types)
     * - Has not been completed (completedTime === null)
     * - Has not been started (startTime === null)
     * - Comes after the current step in the journey steps array
     *
     * @returns {Object|null} Returns:
     *   - The next unstarted standard step object if found
     *   - null if no such step exists (all remaining steps are started/completed, or no journey exists)
     *
     * @example
     * // Get the next step to advance the user to
     * const nextStep = ExamSessionStore.getNextJourneyStep();
     * if (nextStep) {
     *   console.log('Next step:', nextStep.stepId);
     *   // Advance user to this step
     * } else {
     *   console.log('No more steps available');
     * }
     */
    getNextJourneyStep: () => {
      const currentStepId = get().journey?.currentStepId;
      if (get().journey?.steps?.length) {
        const currentIndex = get().journey.steps.findIndex(step => step.id === currentStepId);
        if (currentIndex !== -1) {
          // Find the next standard step that hasn't been started or completed yet
          for (let i = currentIndex + 1; i < get().journey.steps.length; i++) {
            const step = get().journey.steps[i];
            if (step.type === 'standard' && step.completedTime === null && step.startTime === null) {
              return step;
            }
          }
        }
      }
      return null;
    },

    getMostRecentStep: (stepId) => {

      if (!get().journey?.steps?.length) {
        return null;
      }

      // Filter steps by the given stepId and that have been completed
      const matchingSteps = get().journey.steps.filter(
        (step) => step.stepId === stepId && step.completedTime !== null
      );

      if (matchingSteps.length === 0) {
        return null;
      }

      // Sort by completedTime descending (most recent first) and return the first one
      const sortedSteps = matchingSteps.sort((a, b) => {
        const timeA = new Date(a.completedTime).getTime();
        const timeB = new Date(b.completedTime).getTime();
        return timeB - timeA; // Descending order
      });

      return sortedSteps[0];
    },

    setStatus: (status) => {
      //TODO: We may need to add Scheduled here, we need to check to see when we are going to be flipping to In Progress...
      if (config.sessionViewer.video.canHaveLiveVideoStatuses.includes(status)) {
        get().storeState('playerStore').setCanHaveLiveVideo(true);
      } else {
        // if it is already set to true, we need to flip to false...
        if (get().storeState('playerStore').canHaveLiveVideo) {
          get().storeState('playerStore').setCanHaveLiveVideo(false);
        }
      }

      set({ status });
    },

    loadAnnotations: async (types) => {
      // First load all the actual annotation types so we have those...
      const annotationTypes = await get()
        .storeState('appStore')
        .ApiClient.getAnnotationTypes({
          installSid: get().installSid,
        });

      if (annotationTypes.successful) {
        // convert to an object for storage so it is easier to deal with...
        if (
          annotationTypes.data?.results &&
          annotationTypes.data.results.length
        ) {
          const localAnnotationTypes = annotationTypes.data.results.reduce(
            (acc, item) => {
              acc[item.id] = item;

              return acc;
            },
            {}
          );
          set({ annotationTypes: localAnnotationTypes });
        }
      } else {
        //TODO: IF this could not be loaded....we need to do something
      }

      // load all the actual annotations for this session...
      const sessionAnnotations = await get()
        .storeState('appStore')
        .ApiClient.getAnnotations({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
        });

      if (sessionAnnotations.successful) {
        // convert to an object for storage so it is easier to deal with...
        if (
          sessionAnnotations.data?.results &&
          sessionAnnotations.data.results.length
        ) {
          const recordingStartDate = new Date(
            get().sessionData.recordingStartDate
          );

          const localSessionAnnotations = {};

          for (const annotation of sessionAnnotations.data.results) {
            get().loadAnnotation(annotation, localSessionAnnotations);
          }

          set({ annotations: localSessionAnnotations });
        }
      } else {
        //TODO: IF this could not be loaded....we need to do something
      }
    },

    // Helper function to replace an annotation if it exists with the same SID
    replaceOrAddAnnotation: (annotationsList, newAnnotation) => {
      // If the annotation has no SID, just add it
      if (!newAnnotation.sid) {
        return [...annotationsList, newAnnotation];
      }

      // Check if an annotation with the same SID already exists
      const existingIndex = annotationsList.findIndex(
        (item) => item.sid === newAnnotation.sid
      );

      if (existingIndex >= 0) {
        // Replace the existing annotation
        const updatedList = [...annotationsList];
        updatedList[existingIndex] = newAnnotation;
        return updatedList;
      } else {
        // Add as a new annotation
        return [...annotationsList, newAnnotation];
      }
    },

    addAnnotation: async ({ typeId, details, createdById, metadata }) => {
      const payload = {
        installSid: get().installSid,
        examSid: get().examSid,
        sessionSid: get().sessionSid,
        payload: {
          typeId,
          details,
          createdById,
          metadata,
        },
      };

      // Add an event that it was successfully modified
      const annotationResult = await get().storeState('appStore').ApiClient.addAnnotation(payload);

      if (annotationResult.successful) {
        return annotationResult.data;
      }

      console.error('Failed to add annotation:', annotationResult);
      return null;
    },

    loadAnnotation: (annotation, sessionAnnotations) => {
      const recordingStartDate = new Date(get().sessionData.recordingStartDate);

      // based on the type, get the type so we can add it to the proper category...
      const type = get().annotationTypes[annotation.typeId];

      // calculate the start...
      const endDate = new Date(annotation.createdDate);
      annotation.start = Math.floor((endDate - recordingStartDate) / 1000);
      // TODO: Need to figure this out...
      annotation.color = 'info';
      annotation.type = type;

      if (!sessionAnnotations) {
        const localAnnotations = { ...get().annotations };
        if (!localAnnotations[type.categoryId]) {
          localAnnotations[type.categoryId] = [];
        }

        // Use the helper function instead of push
        localAnnotations[type.categoryId] = get().replaceOrAddAnnotation(
          localAnnotations[type.categoryId],
          annotation
        );
        set({ annotations: localAnnotations });
      } else {
        if (!sessionAnnotations[type.categoryId]) {
          sessionAnnotations[type.categoryId] = [];
        }
        // Use the helper function for the session annotations too
        sessionAnnotations[type.categoryId] = get().replaceOrAddAnnotation(
          sessionAnnotations[type.categoryId],
          annotation
        );
      }

      /*
        Start: Take some additional specific actions for certain annotations...
      */
      // Handle hand raise event
      if (annotation.typeId === 'hand-raised') {

        let isRaised = true;
        // if there is an end to this, then this is the end of the hand raise
        if (annotation.range?.end || annotation.duration > 0) {
          // This is the end of a hand raise - lower the hand
          isRaised = false;
        }
        // set if the hand is raised
        get().storeState('communicationStore').setHandRaised(isRaised);
      }

      /*
        End: Take some additional specific actions for certain annotations...
      */
    },

    // Remove an annotation by SID
    getAnnotationFromStore: (annotationSid) => {
      const localAnnotations = { ...get().annotations };
      // loop all the categories to find the annotation object
      for (const categoryId in localAnnotations) {
        if (!localAnnotations[categoryId]) {
          continue;
        }
        const annotation = localAnnotations[categoryId].find(
          (item) => item.sid === annotationSid
        );
        if (annotation) {
          return annotation;
        }
      }
      return null;
    },

    deleteAnnotation: async (annotationSid) => {
      // get the annotation to ensure it is valid...
      const annotation = get().getAnnotationFromStore(annotationSid);
      if (!annotation) {
        // we are going to return true to avoid any potential race conditions with another proctoring removing it first...
        return { successful: true };
      }

      const deleteAnnotationResult = await get()
        .storeState('appStore')
        .ApiClient.deleteAnnotation({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
          annotationSid: annotationSid,
        });

      if (deleteAnnotationResult.successful) {
        // remove the annotation from the store
        get().removeAnnotation(annotationSid);
        return { successful: true };
      } else {
        return { successful: false };
      }
    },

    updateAnnotation: async (annotationSid, payload) => {
      // get the annotation to ensure it is valid...
      const annotation = get().getAnnotationFromStore(annotationSid);
      const localAnnotations = { ...get().annotations };
      if (!annotation) {
        // we are going to return true to avoid any potential race conditions with another proctoring removing it first...
        return { successful: true };
      }

      const updateAnnotationResult = await get()
        .storeState('appStore')
        .ApiClient.updateAnnotation({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
          annotationSid: annotationSid,
          payload,
        });

      if (updateAnnotationResult.successful) {
        // Update the annotation with the new payload
        const updatedAnnotation = {
          ...annotation,
          ...payload,
        };

        // Update the annotation in the local store
        localAnnotations[annotation.type.categoryId] =
          get().replaceOrAddAnnotation(
            localAnnotations[annotation.type.categoryId],
            updatedAnnotation
          );

        // Update the annotations in the store
        set({ annotations: localAnnotations });

        // If is the same annotation as the one being managed...update the managing one too...
        if (get().managingAnnotation?.sid === annotationSid) {
          set({ managingAnnotation: updatedAnnotation });
        }

        return { successful: true };
      } else {
        return { successful: false };
      }
    },

    // Remove an annotation by SID
    removeAnnotation: (annotationSid) => {
      const localAnnotations = { ...get().annotations };
      // loop all the categories to find the annotation object
      for (const categoryId in localAnnotations) {
        if (!localAnnotations[categoryId]) {
          continue;
        }
        const annotation = localAnnotations[categoryId].find(
          (item) => item.sid === annotationSid
        );
        if (annotation) {
          // remove the annotation
          localAnnotations[categoryId] = localAnnotations[categoryId].filter(
            (item) => item.sid !== annotationSid
          );
          set({ annotations: localAnnotations });
        }
      }
    },

    setManagingAnnotation: (annotation) => {
      set({ managingAnnotation: annotation });
    },

    /**
     * Gets all annotations from all categories combined into a single array
     * and sorted by createdDate with newest annotations first
     *
     * @param {Object} options - Filter options
     * @param {boolean} [options.onlyEnabled=true] - Only include annotations from enabled categories
     * @param {string|string[]} [options.types] - Filter by specific annotation type(s)/categoryId (e.g., 'flags', ['flags', 'notes'])
     * @returns {Array} Combined array of all annotations sorted by createdDate (newest first)
     *
     * @example
     * // Get all enabled annotations
     * getAnnotations({ onlyEnabled: true })
     *
     * @example
     * // Get only flags
     * getAnnotations({ types: 'flags' })
     *
     * @example
     * // Get flags and notes
     * getAnnotations({ types: ['flags', 'notes'] })
     *
     * @example
     * // Get all annotations regardless of enabled status
     * getAnnotations({ onlyEnabled: false })
     */
    getAnnotations: (options = {}) => {
      const { onlyEnabled = false, types = null } = options;

      const allAnnotations = [];
      let annotationsByType = { ...get().annotations };

      // Filter by specific type(s) if provided
      if (types) {
        const typeArray = Array.isArray(types) ? types : [types];
        const filteredTypes = {};
        typeArray.forEach(type => {
          if (annotationsByType[type]) {
            filteredTypes[type] = annotationsByType[type];
          }
        });
        annotationsByType = filteredTypes;
      }

      // Filter out any annotations that are not enabled
      if (onlyEnabled) {
        const enabledAnnotations = get().storeState('playerStore').displayMarkers;

        Object.keys(annotationsByType).forEach((categoryId) => {
          if (!enabledAnnotations.includes(categoryId)) {
            delete annotationsByType[categoryId];
          }
        });
      }

      // Loop through each annotation category
      Object.keys(annotationsByType).forEach((categoryId) => {
        // Skip if the category doesn't exist or is empty
        if (
          !annotationsByType[categoryId] ||
          !annotationsByType[categoryId].length
        ) {
          return;
        }

        // Add all annotations from this category to the combined array
        annotationsByType[categoryId].forEach((annotation) => {
          allAnnotations.push({
            ...annotation,
            categoryId, // Add the categoryId to each annotation for reference
          });
        });
      });

      // Sort by createdDate, newest first
      return allAnnotations.sort((a, b) => {
        const dateA = new Date(a.createdDate);
        const dateB = new Date(b.createdDate);
        return dateB - dateA; // Descending order (newest first)
      });
    },

    loadConnectionInfo: async () => {
      // First load all the actual annotation types so we have those...
      const connectionInfo = await get()
        .storeState('appStore')
        .ApiClient.getParticipants({
          installSid: get().installSid,
          examSid: get().examSid,
          sessionSid: get().sessionSid,
          expand: 'network'
        });

      if (connectionInfo.successful) {
        set({ connectionInfo: connectionInfo.data });
      } else {
        //TODO: IF this could not be loaded....we need to do something
      }
    },

    /**
     * Loads modality options for the current exam session.
     * If options are already cached in the store, returns them immediately.
     * Otherwise, fetches from ApiClient and caches the result.
     * @async
     * @function loadModalityOptions
     * @returns {{successful: boolean, data: Array}} response object with success flag and array of modality options
     */
    loadModalityOptions: async () => {
      // Only load the modality options if the object is not already populated...
      if (get().modalityOptions?.length) {
        return { successful: true, data: get().modalityOptions };
      }
      const modalityOptions = await get()
        .storeState('appStore')
        .ApiClient.getModalityOptions({
          proctorTypeSubCategorySid: get().sessionData.proctorTypeSid,
        });

      if (modalityOptions.successful) {
        set({ modalityOptionGroups: modalityOptions.data });
        return { successful: true, data: modalityOptions.data };
      } else {
        //TODO: IF this could not be loaded....we need to do something
      }
    },

    invokeAction: async (payload) => {
      try {
        const appStore = get().storeState('appStore');

        // Check if room and localParticipant exist before attempting RPC
        if (!appStore?.room?.localParticipant || !appStore.isRoomConnected) {
          console.error('invokeAction: Room or localParticipant not available');
          return {
            successful: false,
            data: null,
            error: 'Room connection not available'
          };
        }

        const actionResult = await appStore.room.localParticipant.performRpc({
          destinationIdentity: get().sessionSid,
          method: 'addAction',
          payload: JSON.stringify(payload),
        });

        const responseObject = JSON.parse(actionResult);
        return {
          successful: responseObject.success,
          data: null,
          error: responseObject?.error ? responseObject.error : null,
        };
      } catch (e) {
        console.error('invokeAction: Error executing action', e);
        return {
          successful: false,
          data: null,
          error: e.message || 'Unknown error occurred'
        };
      }
    },

    clearAnnotationType: (type) => {
      //const updatedAnnotations = { ...get().annotations };
      //delete updatedAnnotations[type];
      //set({annotations: updatedAnnotations})
    },

    setIncidentReport: (incidentReport) => {
      set({ incidentReport });
    },

    setOverlay: ({ name, last = false }) => {
      // ensure the overlay is not already in the stack, if so, do nothing...
      if (get().overlayStack.includes(name)) {
        return;
      }

      // Add to top (index 0) by default, or bottom (end of array) if last=true
      const updatedStack = last
        ? [...get().overlayStack, name]  // Add at bottom (end of array)
        : [name, ...get().overlayStack]; // Add at top (index 0) - default

      // Current overlay is always the first item (index 0 = top of stack)
      const currentOverlay = updatedStack[0];

      set({
        overlayStack: updatedStack,
        currentOverlay: currentOverlay
      });
    },

    removeOverlay: (overlay) => {
      // Ensure there are overlays in the stack to avoid errors
      if (get().overlayStack.length === 0) {
        return;
      }

      // If overlay is not passed, remove the top overlay (index 0)
      if (!overlay) {
        overlay = get().overlayStack[0];
      }

      // Remove the overlay from the stack
      const updatedStack = get().overlayStack.filter((o) => o !== overlay);

      // Get the next overlay (index 0 = top of stack) or null if stack is empty
      const currentOverlay = updatedStack.length > 0 ? updatedStack[0] : null;

      // Update both the stack and current overlay
      set({
        overlayStack: updatedStack,
        currentOverlay: currentOverlay
      });
    },

    handleLastConnectionChangeUpdated: (last, current) => {
      let overlay = null;

      // Deep compare to check if there's an actual change
      const currentState = get().lastConnectionChange;
      if (currentState &&
        currentState.type === current.type &&
        currentState.changedAt === current.changedAt &&
        JSON.stringify(currentState.metadata) === JSON.stringify(current.metadata)) {
        return;
      }

      // update the last connection change...
      set({ lastConnectionChange: current });

      if (current.type !== 'connected') {
        overlay = current.type;
        get().setOverlay({ name: overlay });
      } else {
        get().removeOverlay("disconnected");
      }

      // if the current type is ended, we need to tear everything down and show the proper overlay...
      if (current.type === 'ended') {
        // update the overlay to indicate that the session has ended.

        //tear everything down...
        //get().storeState('appStore').room?.disconnect();
      }

      // trigger the event for the last connection change updated...
      get().storeState('eventStore').trigger('onLastConnectionChangeUpdated', { type: current.type, current, last });
    },

    getIncidentReport: async () => {
      const incidentReports = await get()
        .storeState('appStore')
        .ApiClient.listIncidentReports({
          sessionSid: get().sessionSid,
          examSid: get().examSid,
          installSid: get().installSid,
        });

      if (incidentReports.successful) {
        if (incidentReports?.data?.results?.length > 0) {
          get().setIncidentReport(incidentReports.data.results[0]);
        } else {
          get().setIncidentReport(null);
        }
      } else {
        //TODO: Handle the error
      }
    },

    getHlsAuthenticationToken: () => {
      return getMediaAuthToken(get().storeState('appStore').spToken);
    },

    setNoise: ({ hasNoise, participant }) => {
      // if the noise is already set to the same value, don't do anything
      if (get().noise.hasNoise === hasNoise) {
        return;
      }

      const participantInfo = {
        identity: participant.identity,
        id: participant.sid,
        participantSid: participant?.participantInfo?.attributes?.participantSid,
        type: participant?.participantInfo?.attributes?.type,
      }

      // Fire event to notify the UI that the noise state has changed
      get().storeState('eventStore').trigger('onNoiseChange', { hasNoise, audioLevel: participant.audioLevel, participantInfo });

      set({ noise: { hasNoise, participantInfo: participantInfo } });
    },

    /**
     * Ends the exam session with a specified reason
     *
     * Sends an action to end the session, optionally forcing cancellation if reconnection
     * is not allowed. Triggers the onSessionEnded event for downstream handling.
     *
     * @param {Object} params - End session parameters
     * @param {string} params.reasonId - The reason code for ending the session
     * @param {string} params.reasonText - Human-readable reason text displayed to the test taker
     * @param {string} [params.notes=null] - Additional notes about why the session ended
     * @param {string} params.transitionToStatus - Target status ('reconnect' or 'cancelled')
     * @returns {Promise<{successful: boolean, data: any}>} Result of the end session operation
     *
     * @fires onSessionEnded - Triggered after session is ended
     * @event onSessionEnded
     * @type {Object}
     * @property {string} reasonId - The reason code for ending the session
     * @property {string} reasonText - Human-readable reason text
     * @property {string} notes - Additional notes about the session end
     * @property {boolean} forceCancel - Whether the session was force cancelled (no reconnection allowed)
     *
     * @example
     * const result = await endSession({
     *   reasonId: 'environment-not-compliant',
     *   reasonText: 'Environment Not Compliant',
     *   notes: 'Background noise detected',
     *   transitionToStatus: 'reconnect'
     * });
     */
    endSession: async ({ reasonId, reasonText, notes = null, transitionToStatus }) => {

      let forceCancel = false;

      // add action...
      if (transitionToStatus) {
        forceCancel = transitionToStatus.toLowerCase() === 'cancelled';
      }

      await get().sync.client.addAction({
        action: 'endSession',
        disconnectReason: reasonId,
        notes,
        forceCancel,
        popup: {
          body: reasonText
        },
      });

      //get().storeState('eventStore').trigger('onSessionEnded', { reasonId, reasonText, notes, forceCancel });
      return { successful: true };
    },

    updateSessionJourney: async (payload) => {
      const modifySessionJourneyStepResponse = await get()
        .storeState('appStore')
        .ApiClient.modifySessionJourneyStep({
          installSid: get().installSid,
          examSid: get().examSid, //TODO: This is not used
          sessionSid: get().sessionSid,
          payload,
        });

      if (modifySessionJourneyStepResponse.successful) {
        return { successful: true, data: modifySessionJourneyStepResponse.data };
      } else {
        return { successful: false, data: null, error: modifySessionJourneyStepResponse.error };
      }
    },

    getUnfinishedJourneyStepIds: () => {
      const journey = get().journey;
      const steps = journey?.steps || [];
      return steps?.filter(step => !step.completedTime)?.map(step => step.stepId) || [];
    },

    /**
     * Checks if a specific journey step is unfinished
     *
     * @param {string} stepId - The ID of the journey step to check
     * @returns {boolean} True if the step is unfinished (no completedTime), false otherwise
     *
     * @example
     * const isUnfinished = isJourneyStepUnfinished('identity-verification');
     * if (isUnfinished) {
     *   // Show UI for incomplete step
     * }
     */
    isJourneyStepUnfinished: (stepId) => {
      const unfinishedSteps = get().getUnfinishedJourneyStepIds();
      return unfinishedSteps.includes(stepId);
    },

    sendToStep: async ({ stepIds = [], unfocusSession = false, pushUserToNextStep = true, completeCurrentStep = false }) => {

      // get all the currently unfinished steps, this is done by checking the current session journey for all that have no completeddate...
      const unfinishedStepIds = get().getUnfinishedJourneyStepIds();

      const steps = [];
      stepIds.forEach(stepId => {
        if (!unfinishedStepIds.includes(stepId)) {
          // Only add steps that are not already in the unfinished list
          steps.push({
            stepId: stepId,
            action: "add"
          });
        }
      });

      // if we are unfocusing the session, we need to also add the waiting for proctor step back so the are put back in the waiting for proctor queue...
      if (unfocusSession && !unfinishedStepIds.includes("WaitingForProctor")) {
        steps.push({
          stepId: "WaitingForProctor",
          action: "add"
        });
      }

      const payload = {
        addedBy: get().storeState('appStore')?.user?.username || 'proctor',
        pushUserToNextStep,
        completeCurrentStep,
        steps: steps
      };

      const updateSessionJourneyResponse = await get().updateSessionJourney(payload);

      if (updateSessionJourneyResponse.successful) {
        // fire the event that the session was sent back to an onboarding step...
        get().storeState('eventStore').trigger('onSentBackToOnboardingStep', { stepIds, unfocusSession });
        return updateSessionJourneyResponse;
      } else {
        //TODO: Do something with the error...
      }
    },

    /**
     * Update a user interaction in the sync document
     *
     * This function replaces the user interaction entry in the sync document's
     * userInteractions object for the specified user. The entire userInteractions
     * object is replaced with only the current user's data, automatically adding
     * the user type and updatedAt timestamp.
     *
     * **Note:** This replaces the entire userInteractions object in the sync document,
     * not just the specific user's entry. This triggers the onUserInteraction event
     * which updates the session store with the new interaction data.
     *
     * @async
     * @function updateUserInteraction
     * @param {string} userKeySid - The SID of the user key to update (typically user.sid)
     * @param {Object} interactionData - The interaction data object to store
     * @param {string} [interactionData.interactionType] - Type of interaction (e.g., 'start-focus', 'start-review', 'approved')
     * @param {boolean} [interactionData.isFocused] - Whether the user is currently focused on this session
     * @param {string} [interactionData.firstName] - User's first name
     * @param {string} [interactionData.lastName] - User's last name
     * @returns {Promise<{successful: boolean, error?: string}>}
     *
     * @example
     * // Update focus interaction
     * await examSessionStore.getState().updateUserInteraction('PU123abc', {
     *   interactionType: 'start-focus',
     *   isFocused: true,
     *   firstName: 'John',
     *   lastName: 'Doe'
     * });
     *
     * @example
     * // Update review interaction
     * await examSessionStore.getState().updateUserInteraction('PU456def', {
     *   interactionType: 'start-review',
     *   firstName: 'Jane',
     *   lastName: 'Smith'
     * });
     */
    updateUserInteraction: async (interactionData) => {
      try {
        const syncClient = get().sync?.client;
        if (!syncClient) {
          console.error('updateUserInteraction: Sync client not available');
          return { successful: false, error: 'Sync client not available' };
        }
        if (get().storeState('appStore')?.user?.sid) {
          const userKeySid = get().storeState('appStore')?.user?.sid;

          const userInteractions = {};
          userInteractions[userKeySid] = {
            ...interactionData,
            type: get().storeState('appStore')?.user?.type,
            updatedAt: Date.now()
          };

          // Mutate the sync document
          await get().persist({ userInteractions });

          return { successful: true };
        } else {
          return { successful: false, error: 'User SID not available' };
        }
      } catch (error) {
        console.error('updateUserInteraction: Error updating user interaction', error);
        return { successful: false, error: error.message || 'Unknown error occurred' };
      }
    },

    /**
     * Set audio chat enabled state
     *
     * Updates the audio chat state for the current user, triggers the onAudioChatChange event,
     * and updates the user interaction in the sync document with the talking state.
     *
     * @async
     * @function setAudioChatEnabled
     * @param {boolean} enabled - Whether audio chat is enabled (true) or disabled (false)
     * @returns {Promise<{successful: boolean, error?: string}>}
     *
     * @example
     * // Enable audio chat
     * await examSessionStore.getState().setAudioChatEnabled(true);
     *
     * @example
     * // Disable audio chat
     * await examSessionStore.getState().setAudioChatEnabled(false);
     */
    setAudioChatEnabled: async (enabled) => {
      try {
        const appStore = get().storeState('appStore');
        const eventStore = get().storeState('eventStore');
        const userSid = appStore?.user?.sid;

        if (!userSid) {
          console.error('setAudioChatEnabled: User SID not available');
          return { successful: false, error: 'User SID not available' };
        }

        // Trigger the audio chat change event
        eventStore.trigger('onAudioChatChange', { enabled });

        // Update user interaction with talking state
        const interactionData = {
          isTalking: enabled,
          isTalkingChangeAt: Date.now(),
        };

        const result = await get().updateUserInteraction(interactionData);
        return result;
      } catch (error) {
        console.error('setAudioChatEnabled: Error setting audio chat state', error);
        return { successful: false, error: error.message || 'Unknown error occurred' };
      }
    },

    /**
     * Get a user interaction from the sync document
     *
     * Retrieves a specific user interaction entry from the sync document.
     *
     * @function getUserInteraction
     * @param {string} userKeySid - The SID of the user key to retrieve
     * @returns {Object|null} The user interaction object or null if not found
     *
     * @example
     * const interaction = examSessionStore.getState().getUserInteraction('userKey_123');
     * if (interaction) {
     *   console.log('Last interaction:', interaction.lastInteraction);
     * }
     */
    getUserInteractions: (userKeySid) => {
      const userInteractions = get().sync?.doc?.userInteractions || {};
      return userInteractions[userKeySid] || null;
    },

    /**
     * Get a user interaction from the sync document
     *
     * Retrieves a specific user interaction entry from the sync document.
     *
     * @function getUserInteraction
     * @param {string} userKeySid - The SID of the user key to retrieve
     * @returns {Object|null} The user interaction object or null if not found
     *
     * @example
     * const interaction = examSessionStore.getState().getUserInteraction('userKey_123');
     * if (interaction) {
     *   console.log('Last interaction:', interaction.lastInteraction);
     * }
     */
    getAllUserInteractions: () => {
      return get().sync?.doc?.userInteractions || {};
    },

    /**
     * Remove a user interaction from the sync document
     *
     * Removes a specific user interaction entry from the sync document.
     *
     * @async
     * @function removeUserInteraction
     * @param {string} userKeySid - The SID of the user key to remove
     * @returns {Promise<{successful: boolean, error?: string}>}
     *
     * @example
     * await examSessionStore.getState().removeUserInteraction('userKey_123');
     */
    removeUserInteraction: async (userKeySid) => {
      try {
        const syncClient = get().sync?.client;
        if (!syncClient) {
          console.error('removeUserInteraction: Sync client not available');
          return { successful: false, error: 'Sync client not available' };
        }

        // Get the current sync document
        const currentDoc = get().sync?.doc || {};
        const userInteractions = { ...currentDoc.userInteractions } || {};

        // Remove the user interaction
        delete userInteractions[userKeySid];

        // Mutate the sync document
        await get().persist({
          userInteractions
        });

        return { successful: true };
      } catch (error) {
        console.error('removeUserInteraction: Error removing user interaction', error);
        return { successful: false, error: error.message || 'Unknown error occurred' };
      }
    },

    /**
   * Persists a session update to the session sync document.
   *
   * @param {Object} data - The data associated with the session to update.
   * @returns {Promise} - Resolves with the result of the sync operation.
   */
    persist: async (data) => {
      const result = await get().sync.client.setSync(data);
      return result;
    },

    setupSyncListeners: () => {
      get().sync.emitter.on(SESSION_EVENTS.SYNC_DOC_UPDATED, function (data) {
        //console.log('SYNC_DOC_UPDATED', data);
      });

      get().sync.emitter.on(
        SESSION_EVENTS.SYNC_DOC_UPDATED_WITH_PREVIOUS_DATA,
        function (data) {
          // START:update the current modality options if things were properly updated...
          const updatedCurrentModalityOptions =
            data.currentData.currentModalityOptions;
          const previousCurrentModalityOptions =
            data.previousData.currentModalityOptions;
          if (
            updatedCurrentModalityOptions !== previousCurrentModalityOptions
          ) {
            // update the current modality options
            set({ currentModalityOptions: updatedCurrentModalityOptions });
          }
          // END: update the current modality options

          // START: Check for user interaction changes
          const updatedUserInteractions = data.currentData.userInteractions;
          const previousUserInteractions = data.previousData.userInteractions;

          if (JSON.stringify(updatedUserInteractions) !== JSON.stringify(previousUserInteractions)) {
            // Trigger event for user interaction changes
            get().storeState('eventStore').trigger('onUserInteraction', {
              current: updatedUserInteractions,
              previous: previousUserInteractions
            });
          }
          // END: Check for user interaction changes

          //console.log('SYNC_DOC_UPDATED_WITH_PREVIOUS_DATA', data);
        }
      );

      // START: last connection change updated...
      get().sync.emitter.on(
        SESSION_EVENTS.LAST_CONNECTION_CHANGE_UPDATED,
        function (data) {
          //console.log('LAST_CONNECTION_CHANGE_UPDATED', data);
          get().handleLastConnectionChangeUpdated(data.oldValue, data.newValue);
        }
      );



      // START: extension actions added...
      get().sync.emitter.on(
        SESSION_EVENTS.EXTENSION_ACTIONS_ADDED,
        function (data) {
          //console.log('EXTENSION_ACTIONS_ADDED', data);
        }
      );

      get().sync.emitter.on(
        SESSION_EVENTS.SESSION_FORCE_CLOSED,
        function (data) {
          //console.log('SESSION_FORCE_CLOSED', data);
        }
      );

      get().sync.emitter.on(
        SESSION_EVENTS.SESSION_JOURNEY_STEP_ID_UPDATED,
        async function (data) {
          await get().refreshSessionJourney();

          // if the old value was either selfie or the idcheck, reload identity validation
          if (data?.oldValue?.stepId === 'SelfiePhoto' || data?.oldValue?.stepId === 'IdCheck') {
            await get().loadIdentityValidation();
          }

          //console.log('SESSION_JOURNEY_STEP_ID_UPDATED', data);
        }
      );

      get().sync.emitter.on(SESSION_EVENTS.STATUS_UPDATED, function (data) {
        // update the local status...
        get().setStatus(data.newValue);
      });
    },
  }));
